// Ring construction and compatibility checking (P0.3).
//
// Allocation and validation live here, away from the hot paths in spsc-ring.ts. Everything in
// this file runs once, when a ring is created or attached to — never per block.

import {
  int32IndexOf,
  RING_HEADER,
  RING_HEADER_BYTES,
  RING_MAGIC,
  type RingLayout,
} from '../types/ring-layout';

const HEADER_INT32_LENGTH = RING_HEADER_BYTES / 4;

const MAGIC_AT = int32IndexOf(RING_HEADER.MAGIC);
const VERSION_AT = int32IndexOf(RING_HEADER.VERSION);
const CAPACITY_AT = int32IndexOf(RING_HEADER.CAPACITY_SLOTS);
const SLOT_BYTES_AT = int32IndexOf(RING_HEADER.SLOT_BYTES);

/**
 * Allocates a SharedArrayBuffer for a layout and stamps its header.
 *
 * Sequences start at zero: a fresh SharedArrayBuffer is already zero-filled, so there is
 * nothing to write for them.
 */
export function createRing(layout: RingLayout): SharedArrayBuffer {
  const sab = new SharedArrayBuffer(layout.totalBytes);
  const header = new Int32Array(sab, 0, HEADER_INT32_LENGTH);

  header[MAGIC_AT] = RING_MAGIC;
  header[VERSION_AT] = layout.version;
  header[CAPACITY_AT] = layout.capacitySlots;
  header[SLOT_BYTES_AT] = layout.slotBytes;

  return sab;
}

/**
 * Refuses a buffer that does not match the layout the caller expects.
 *
 * This is where P0.2's version byte earns its place. The two sides of a ring live in bundles
 * that ship and cache separately (P0.4 makes that physical), so a browser holding a stale
 * worklet against a fresh main thread is a real deployment state, not a hypothetical. Without
 * this check, a moved field means the worklet reads a gain where a frequency used to be and
 * the failure arrives as a noise burst. With it, the ring refuses to attach.
 */
export function assertRingCompatible(sab: SharedArrayBuffer, layout: RingLayout): void {
  if (sab.byteLength !== layout.totalBytes) {
    throw new RangeError(
      `${layout.name} ring: buffer is ${String(sab.byteLength)} bytes, layout expects ${String(layout.totalBytes)}`,
    );
  }

  const header = new Int32Array(sab, 0, HEADER_INT32_LENGTH);

  if (header[MAGIC_AT] !== RING_MAGIC) {
    throw new TypeError(
      `${layout.name} ring: buffer is not a SYNAPSE ring (bad magic). Was it created by createRing?`,
    );
  }
  if (header[VERSION_AT] !== layout.version) {
    throw new TypeError(
      `${layout.name} ring: buffer is layout version ${String(header[VERSION_AT])}, this build expects ${String(layout.version)}. ` +
        `One side of the ring is running stale code.`,
    );
  }
  if (header[CAPACITY_AT] !== layout.capacitySlots) {
    throw new RangeError(
      `${layout.name} ring: buffer holds ${String(header[CAPACITY_AT])} slots, layout expects ${String(layout.capacitySlots)}`,
    );
  }
  if (header[SLOT_BYTES_AT] !== layout.slotBytes) {
    throw new RangeError(
      `${layout.name} ring: buffer has ${String(header[SLOT_BYTES_AT])}-byte slots, layout expects ${String(layout.slotBytes)}`,
    );
  }
}
